import React, { useContext, useMemo } from 'react';
import { FriendsContext } from '../contexts/FriendsContext';
import { ResultsContext } from '../contexts/ResultsContext';
import { ThemeContext } from '../contexts/ThemeContext';
import FriendCard from './FriendCard';
import PlayerComparison from './PlayerComparison';
import { filterIgnoredGames } from '../utils/ignoredGames';
import './FriendsView.css';

/**
 * FriendsView component displays a list of friends and their tournament performance
 * 
 * @param {Object} props - Component props
 * @param {string} props.username - Current user's username
 * @param {string} props.tournamentId - Current tournament ID
 */
const FriendsView = ({ username, tournamentId, sortOrder = 'default' }) => {
  const { friendsList, loading: friendsLoading } = useContext(FriendsContext);
  const { results, loading: resultsLoading } = useContext(ResultsContext);
  const { theme } = useContext(ThemeContext);

  // Combine current user and friends list for comparison
  const allPlayersToShow = useMemo(() => {
    const list = [...friendsList];

    // Add current user to the top if they're not already in the friends list
    if (username && !friendsList.some(f => f.toLowerCase() === username.toLowerCase())) {
      return [username, ...list];
    }

    // If current user is in the friends list, move them to the top
    if (username) {
      const userIndex = list.findIndex(f => f.toLowerCase() === username.toLowerCase());
      if (userIndex !== -1) {
        const user = list.splice(userIndex, 1)[0];
        return [user, ...list];
      }
    }

    return list;
  }, [friendsList, username]);

  // Filter games based on ignored games list
  const filteredGames = useMemo(() => {
    if (!results?.games) return [];
    return filterIgnoredGames(results.games, tournamentId);
  }, [results?.games, tournamentId]);

  const [selectedPlayers, setSelectedPlayers] = React.useState([]);

  const isLoading = friendsLoading || resultsLoading;

  const handleCompare = (friendUsername) => {
    setSelectedPlayers(prev => {
      if (prev.includes(friendUsername)) {
        return prev.filter(p => p !== friendUsername);
      }
      if (prev.length >= 2) {
        // If 2 already selected, replace the second one or just ignore?
        // Let's replace the second one to keep it simple
        return [prev[0], friendUsername];
      }
      return [...prev, friendUsername];
    });
  };

  const handleCloseComparison = () => {
    setSelectedPlayers([]);
  };

  if (isLoading) {
    return (
      <div className={`friends-view-container ${theme}`}>
        <div className="friends-loading">Loading friends performance...</div>
      </div>
    );
  }

  if (selectedPlayers.length === 2) {
    return (
      <div className={`friends-view-container ${theme}`}>
        <PlayerComparison
          player1={selectedPlayers[0]}
          player2={selectedPlayers[1]}
          games={results?.games || []}
          theme={theme}
          onClose={handleCloseComparison}
        />
      </div>
    );
  }

  return (
    <div className={`friends-view-container ${theme}`}>
      <div className="friends-list">
        {allPlayersToShow.length > 0 ? (
          allPlayersToShow.map((friendUsername) => (
            <FriendCard
              key={friendUsername}
              username={friendUsername}
              games={filteredGames}
              theme={theme}
              sortOrder={sortOrder}
              isCurrentUser={friendUsername.toLowerCase() === username.toLowerCase()}
              isSelected={selectedPlayers.includes(friendUsername)}
              onCompare={() => handleCompare(friendUsername)}
            />
          ))
        ) : (
          <div className="no-friends-message">
            <p>You haven't added any friends yet.</p>
            <div className="add-friends-tip">
              Tip: Go to any game's leaderboard and click the "+" button next to a player to add them as a friend.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendsView;
